import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChildren,
  QueryList,
  ElementRef,
  Injector,
  OnChanges
} from '@angular/core';

import { ComposableInputComponentBlock, ComposableInputFocusEvent, ComposableInputMessage, KeyboardKey } from './interfaces';
import { UtilsService } from '../../services/utils';

@Component({
  selector: 'vi-ui-composable-input',
  templateUrl: './composable-input.component.html',
  styleUrls: ['./composable-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ComposableInputComponent implements OnChanges {
  @Input() messages: ComposableInputMessage[] = [];
  @Input() public placeholder: string;

  @Output() messagesChange = new EventEmitter<ComposableInputMessage[]>();
  @Output() componentDeleted = new EventEmitter<ComposableInputComponentBlock>();
  @Output() currentFocused = new EventEmitter<ComposableInputFocusEvent>();

  @ViewChildren('textInput') textInputs!: QueryList<ElementRef<HTMLInputElement>>;
  public isPlaceholder = false;
  public isCursorAtStart: boolean;
  public isCursorAtEnd: boolean;
  public readonly INPUT_FONT_SIZE = 14;

  constructor(public injector: Injector, private utilsService: UtilsService) {}

  public ngOnChanges(): void {
    this.handleMessagesChange();
  }

  public isString(msg: ComposableInputMessage): boolean {
    return typeof msg === 'string';
  }

  public onTextInput(event: Event, index: number): void {
    const value = (event?.target as HTMLInputElement)?.value + '';
    if (this.messages[index] !== value) {
      this.messages[index] = value;
      this.removeEmptyMsg();
      this.messagesChange.emit(this.messages);
      this.handleMessagesChange();
    }
  }

  public onTextKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;

    // Use cursor position for tracking location before keyup event occurs
    this.isCursorAtStart = cursorPosition === 0 && input.selectionEnd === 0;
    this.isCursorAtEnd = cursorPosition === input.value.length && input.selectionEnd === input.value.length;
  }

  public onTextKeyup(event: KeyboardEvent, index: number): void {
    // Discard on esc & enter
    if (event?.key === KeyboardKey.Escape || event?.key === KeyboardKey.Enter) {
      return;
    }

    const keyHandlers: Record<string, (event: KeyboardEvent) => void> = {
      [KeyboardKey.ArrowRight]: () => this.handleArrowRight(event, index, this.isCursorAtEnd),
      [KeyboardKey.ArrowLeft]: () => this.handleArrowLeft(event, index, this.isCursorAtStart),
      [KeyboardKey.Backspace]: () => this.handleBackspace(event, index, this.isCursorAtStart),
      [KeyboardKey.Delete]: () => this.handleDelete(event, index, this.isCursorAtEnd),
      [KeyboardKey.End]: () => this.focusLastTextInput(),
      [KeyboardKey.Home]: () => this.focusFirstTextInput()
    };
    const handler = keyHandlers[event.key];
    if (handler) {
      handler(event);
    }

    const input = event.target as HTMLInputElement;
    this.emitRecalculatedRange(input.selectionStart || 0, index);
  }

  public onClickInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    this.emitRecalculatedRange(cursorPosition, index);
  }

  public emitRecalculatedRange(cursorPosition: number, index: number): void {
    // Handle mouse clicks to emit currentFocused
    setTimeout(() => {
      this.currentFocused.emit({
        messageOffset: cursorPosition,
        messageContent: this.messages[index] as string,
        index: index
      });
    }, 0);
  }

  /**
   * Public method to allow parent component to control focus
   */
  public focus() {
    // Wait for the inputs/components to update before managing focus
    setTimeout(() => {
      this.focusLastTextInput();
    });
  }

  public focusMessageIndex(index: number, isStart = true) {
    const textInputIdx = this.getTextInputIndexForMessageIndex(index);
    this.focusTextInput(textInputIdx, isStart);
  }

  public getInputWidth(index: number): string {
    return `${this.utilsService.measureText(this.messages[index] as string, this.INPUT_FONT_SIZE)}px`;
  }

  public handleDelete(event: KeyboardEvent, index: number, isAtEnd: boolean): void {
    // Delete at end
    if (isAtEnd && index < this.messages.length - 1) {
      if (this.isComponent(this.messages[index + 1])) {
        const InEmptyTextBetweenComps =
          index >= 2 &&
          this.isComponent(this.messages[index - 1]) &&
          typeof this.messages[index] === 'string' &&
          (this.messages[index] as string).trim() === '';
        const component = this.messages[index + 1] as ComposableInputComponentBlock;
        this.messages = [...this.messages.slice(0, index + 1), ...this.messages.slice(index + 2)];
        this.handleComponentDeleted(component);
        // focus after the removed component
        const currentInputIdx = this.getTextInputIndexForMessageIndex(index);
        this.focusTextInput(currentInputIdx, InEmptyTextBetweenComps);
      } else {
        // Focus next text input and remove first character
        const nextInputIdx = this.getTextInputIndexForMessageIndex(index + 1);
        this.focusTextInput(nextInputIdx, true);
        this.messages[nextInputIdx] = (this.messages[nextInputIdx] as string).slice(1);
      }
      event.preventDefault();
      return;
    } else if ((index > 0 && (this.messages[index] as string)).length === 1) {
      // it means current message will be empty, focus on prev index
      const prevInputIdx = this.getTextInputIndexForMessageIndex(index - 1);
      this.focusTextInput(prevInputIdx);
    }
  }

  private handleArrowRight(event: KeyboardEvent, index: number, isAtEnd: boolean): void {
    // ArrowRight: skip over component to next text input if at end and next message is a component
    if (isAtEnd && index < this.messages.length - 1) {
      // Find next text input after the component(s)
      for (let i = index + 1; i < this.messages.length; i++) {
        if (this.isString(this.messages[i])) {
          const textInputIdx = this.getTextInputIndexForMessageIndex(i);
          this.focusTextInput(textInputIdx, true);
          event.preventDefault();
          return;
        }
      }
    }
  }

  private handleArrowLeft(event: KeyboardEvent, index: number, isAtStart: boolean): void {
    // ArrowLeft: skip over component to previous text input if at start and previous message is a component
    if (isAtStart && index > 0) {
      // Find previous text input before the component(s)
      for (let i = index - 1; i >= 0; i--) {
        if (this.isString(this.messages[i])) {
          const textInputIdx = this.getTextInputIndexForMessageIndex(i);
          this.focusTextInput(textInputIdx);
          event.preventDefault();
          return;
        }
      }
      // If none found, focus the first input (index 0)
      if (index > 1) {
        this.focusTextInput(0);
        event.preventDefault();
      }
    }
  }

  private handleBackspace(event: KeyboardEvent, index: number, isAtStart: boolean): void {
    if (index > 0 && isAtStart) {
      // remove previous component block if present
      if (this.isComponent(this.messages[index - 1])) {
        const prevInputIdx = this.getTextInputIndexForMessageIndex(index - 2);
        // Check if there are two components with empty text between them before this point
        const hasPrevEmptyTextBetweenComps =
          index >= 3 &&
          this.isComponent(this.messages[index - 3]) &&
          typeof this.messages[index - 2] === 'string' &&
          (this.messages[index - 2] as string).trim() === '';
        const component = this.messages[index - 1] as ComposableInputComponentBlock;
        this.messages = [...this.messages.slice(0, index - 1), ...this.messages.slice(index)];
        this.handleComponentDeleted(component);
        this.focusTextInput(prevInputIdx, hasPrevEmptyTextBetweenComps);
      } else {
        // Focus previous text input and remove last character
        const prevInputIdx = this.getTextInputIndexForMessageIndex(index - 1);
        this.messages[index - 1] = (this.messages[index - 1] as string).slice(0, -1);
        this.focusTextInput(prevInputIdx);
      }
      event.preventDefault();
      return;
    } else if ((index > 0 && (this.messages[index] as string)).length === 1) {
      // it means current message will be empty, focus on prev index
      const prevInputIdx = this.getTextInputIndexForMessageIndex(index - 1);
      this.focusTextInput(prevInputIdx);
    }
  }

  private isComponent(msg: ComposableInputMessage): boolean {
    return typeof msg !== 'string';
  }

  private focusLastTextInput(): void {
    setTimeout(() => {
      const textInputsArr = this.textInputs?.toArray() || [];
      if (textInputsArr.length > 0) {
        const last = textInputsArr[textInputsArr.length - 1].nativeElement;
        last.focus();
        last.setSelectionRange(last.value.length, last.value.length);
      }
    });
  }

  private focusFirstTextInput(): void {
    setTimeout(() => {
      const textInputsArr = this.textInputs?.toArray() || [];
      if (textInputsArr.length > 0) {
        const first = textInputsArr[0].nativeElement;
        first.focus();
        first.setSelectionRange(0, 0);
      }
    });
  }

  /**
   * Focuses the text input at the given index (0-based, or messages.length for trailing input).
   */
  private focusTextInput(index: number, collapseToStart: boolean = false): void {
    setTimeout(() => {
      const arr = this.textInputs?.toArray() || [];
      const el = arr[index]?.nativeElement as HTMLInputElement;

      if (el) {
        el.focus();
        el.setSelectionRange(collapseToStart ? 0 : el.value.length, collapseToStart ? 0 : el.value.length);
        return;
      }
    });
  }

  /**
   * Maps a messages array index (for a text element) to the corresponding index in textInputs.
   * Returns the Nth string in messages up to msgIdx.
   */
  private getTextInputIndexForMessageIndex(msgIdx: number): number {
    return this.messages.slice(0, msgIdx).reduce((count, message) => (typeof message === 'string' ? count + 1 : count), 0);
  }

  private handleMessagesChange(): void {
    const isEmptyMessage = this.messages.length === 0 || (this.messages.length === 1 && this.messages[0] === '');
    this.isPlaceholder = isEmptyMessage && !!this.placeholder;
    if (isEmptyMessage || (this.messages.length > 0 && (this.messages[0] === '\n' || this.isComponent(this.messages[this.messages.length - 1])))) {
      this.messages.push('');
    }
    if (this.messages.length > 0 && this.isComponent(this.messages[0])) {
      // Ensure first message is a string if the first message is a component
      this.messages.unshift('');
    }
    // Ensure there's an empty string between consecutive components
    for (let i = 1; i < this.messages.length; i++) {
      if (this.isComponent(this.messages[i]) && this.isComponent(this.messages[i - 1])) {
        this.messages.splice(i, 0, '');
        i++; // Skip the newly inserted empty string
      }
    }
  }

  private removeEmptyMsg(): void {
    // Remove any empty messages from merged inputs
    this.messages = this.messages.filter(msg => {
      if (typeof msg === 'string') {
        return msg.trim() !== '';
      }
      return true;
    });
  }

  private handleComponentDeleted(componentBlock: ComposableInputComponentBlock) {
    this.componentDeleted.emit(componentBlock);
    this.removeEmptyMsg();
    this.messagesChange.emit(this.messages);
    this.handleMessagesChange();
  }
}
